
import { SolutionData, SolutionDetails } from '@/types/solution';
import { defaultImageService } from '@/services/defaultImageService';

export interface PreviewSession {
  id: string;
  timestamp: number;
  solutionData: SolutionData;
  jsonContent: string;
}

const PREVIEW_STORAGE_KEY = 'solution-previews';
const PREVIEW_EXPIRY_HOURS = 24;

export const generatePreviewId = (): string => {
  return 'preview-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

export const processJsonForPreview = (
  jsonContent: string, 
  solutionName?: string,
  solutionShortDescription?: string,
  solutionCategory?: string,
  solutionTags?: string[],
  imageUrl?: string
): SolutionData | null => {
  try {
    const parsedDetails = JSON.parse(jsonContent);
    
    // Extract metadata from parsed JSON if available
    const metadata = parsedDetails.metadata || {};
    
    // Extract officialWebsite and externalLinks from the parsed JSON
    const officialWebsite = parsedDetails.officialWebsite || null;
    const externalLinks = parsedDetails.externalLinks || [];
    
    // Try different possible image field names from JSON
    const jsonImage = parsedDetails.image || 
      parsedDetails.imageUrl || 
      parsedDetails.image_url || 
      parsedDetails.thumbnail || 
      parsedDetails.photo || 
      parsedDetails.metadata?.imageUrl ||
      null;
    
    // Prioritize the passed-in imageUrl parameter, then JSON image, then cached default
    const image = imageUrl || jsonImage || defaultImageService.getDefaultImageUrlSync();
    
    const solutionData = {
      id: 'preview-' + Date.now(),
      name: metadata.name || solutionName || 'Preview Solution',
      shortDescription: metadata.shortDescription || solutionShortDescription || 'This is a preview of the AI-generated solution page.',
      category: metadata.category || solutionCategory || 'Others (U)',
      tags: metadata.tags || solutionTags || [],
      createdBy: 'Preview User',
      createdAt: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      image: image,
      officialWebsite: officialWebsite,
      externalLinks: externalLinks,
      details: parsedDetails as SolutionDetails
    };
    
    return solutionData;
  } catch (error) {
    console.error('Preview JSON parsing error:', error);
    return null;
  }
};

export const storePreviewData = (id: string, data: SolutionData): void => {
  try {
    const previews = getStoredPreviews();
    
    const previewEntry = {
      data,
      timestamp: Date.now(),
      expires: Date.now() + (PREVIEW_EXPIRY_HOURS * 60 * 60 * 1000)
    };
    
    previews[id] = previewEntry;
    localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(previews));
  } catch (error) {
    console.error('Error storing preview data:', error);
  }
};

export const retrievePreviewData = (id: string): SolutionData | null => {
  try {
    const previews = getStoredPreviews();
    const preview = previews[id];
    
    if (!preview) {
      return null;
    }
    
    // Check if expired
    if (Date.now() > preview.expires) {
      delete previews[id];
      localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(previews));
      return null;
    }
    
    return preview.data;
  } catch (error) {
    console.error('Error retrieving preview data:', error);
    return null;
  }
};

const getStoredPreviews = (): Record<string, any> => {
  try {
    const stored = localStorage.getItem(PREVIEW_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error parsing stored previews:', error);
    return {};
  }
};

export const cleanupExpiredPreviews = (): void => {
  console.log('=== CLEANING UP EXPIRED PREVIEWS (LOCALSTORAGE) ===');
  
  try {
    const previews = getStoredPreviews();
    const now = Date.now();
    let hasChanges = false;
    
    Object.keys(previews).forEach(id => {
      if (previews[id].expires < now) {
        console.log('Removing expired preview:', id);
        delete previews[id];
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(previews));
      console.log('✅ Expired previews cleaned up');
    } else {
      console.log('No expired previews to clean up');
    }
  } catch (error) {
    console.error('❌ Error cleaning up expired previews:', error);
  }
};
